import { useEffect, useMemo, useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';
import { deleteRole, updateRole } from '@mkcore/platform/schema';
import { Button, Card, Checkbox, ErrorState, Field, Input, PageHeader, Skeleton, Switch, Textarea } from '@mkcore/ui';
import { useActionMutation, useErrorMessage } from '@/lib/mutation';
import { useCan, useMe } from '@/lib/session';
import { permissionsQuery, roleQuery } from '@/features/admin/queries';
import { ConfirmButton } from '@/components/confirm';

export const Route = createFileRoute('/_app/admin/roles/$id')({ component: RoleDetail });

function RoleDetail() {
  const { t } = useTranslation();
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const me = useMe();
  const role = useQuery(roleQuery(id));
  const catalog = useQuery(permissionsQuery);
  const canUpdate = useCan('admin:update');
  const canDelete = useCan('admin:delete');
  const message = useErrorMessage();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [tenantWide, setTenantWide] = useState(false);
  const [perms, setPerms] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (!role.data) return;
    setName(role.data.name);
    setDescription(role.data.description);
    setTenantWide(role.data.tenantWideScope);
    setPerms(new Set(role.data.permissionKeys));
  }, [role.data]);
  const save = useActionMutation(updateRole, { invalidate: [['admin', 'role']], success: t('common.saved') });
  const remove = useActionMutation(deleteRole, {
    invalidate: [['admin', 'role']],
    onSuccess: () => void navigate({ to: '/admin/roles' }),
  });

  const byModule = useMemo(() => {
    const m = new Map<string, { key: string; action: string; description: string }[]>();
    for (const p of catalog.data?.items ?? []) m.set(p.module, [...(m.get(p.module) ?? []), p]);
    return [...m.entries()];
  }, [catalog.data]);

  if (role.isError) return <ErrorState title={t('errors.generic')} description={message(role.error)} />;
  if (!role.data || !catalog.data) return <Skeleton className="h-96" />;
  const r = role.data;
  const readOnly = r.isSystem || !canUpdate;

  return (
    <>
      <Link to="/admin/roles" className="mb-3 inline-flex items-center gap-1 text-sm text-fg-2 hover:text-fg-1">
        <ArrowLeft className="size-4" /> {t('roles.title')}
      </Link>
      <PageHeader
        eyebrow={t('nav.roles')}
        title={r.name}
        description={r.isSystem ? t('roles.fixed') : r.description}
        actions={
          !r.isSystem &&
          canDelete && (
            <ConfirmButton
              variant="danger"
              label={t('common.delete')}
              title={t('roles.deleteConfirm', { name: r.name })}
              onConfirm={() => remove.mutateAsync({ input: { id }, ifMatch: r.version })}
            />
          )
        }
      />
      <form
        className="grid gap-6"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate({ ifMatch: r.version, input: { id, name, description, tenantWideScope: tenantWide, permissionKeys: [...perms] } });
        }}
      >
        <Card className="grid max-w-xl gap-4 p-6">
          <Field label={t('common.name')}>{(p) => <Input {...p} value={name} disabled={readOnly} onChange={(e) => setName(e.target.value)} />}</Field>
          <Field label={t('common.details')}>
            {(p) => <Textarea {...p} value={description} disabled={readOnly} onChange={(e) => setDescription(e.target.value)} />}
          </Field>
          <label className="flex items-start gap-3 text-sm">
            <Switch checked={tenantWide} disabled={readOnly || !me.tenantWide} onCheckedChange={setTenantWide} aria-describedby="tw-hint" />
            <span>
              <span className="font-bold">{t('roles.tenantWide')}</span>
              <span id="tw-hint" className="block text-xs text-fg-3">
                {t('roles.tenantWideHint')}
              </span>
            </span>
          </label>
        </Card>
        <Card className="p-6">
          <h2 className="mb-4 text-lg font-bold">{t('roles.permissions')}</h2>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {byModule.map(([module, list]) => (
              <fieldset key={module} className="grid gap-2" disabled={readOnly}>
                <legend className="mb-1 font-mono text-sm font-bold">{module}</legend>
                {list.map((p) => (
                  <label key={p.key} className="flex items-start gap-2 text-sm">
                    <Checkbox
                      checked={perms.has(p.key)}
                      disabled={readOnly || !me.permissions.includes(p.key)}
                      onCheckedChange={(v) => {
                        const next = new Set(perms);
                        if (v === true) next.add(p.key);
                        else next.delete(p.key);
                        setPerms(next);
                      }}
                    />
                    <span>
                      <span className="font-mono text-xs">{p.action}</span>
                      <span className="block text-xs text-fg-3">{p.description}</span>
                    </span>
                  </label>
                ))}
              </fieldset>
            ))}
          </div>
        </Card>
        {!readOnly && (
          <div>
            <Button type="submit" loading={save.isPending}>
              {t('common.save')}
            </Button>
          </div>
        )}
      </form>
    </>
  );
}
